import React, {useEffect, useMemo, useState} from 'react';
import {Autocomplete, Box, Chip, CircularProgress, TextField} from '@mui/material';
import {debounce} from '@mui/material/utils';
import PropTypes from 'prop-types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Checkbox from '@mui/material/Checkbox';

const PAGE_SIZE = 20;
const TAGS_SHOWN = 5;

const PaginatedAutocomplete = (
    {
        fetchFn,
        fetchFnProps = {},
        getOptionLabel,
        value,
        onChange,
        textFieldProps,
        disablePortal = true,
        isSelectionDisabled = false,
        popperPlacement,
        popperModifiers,
        multiple = true
    }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [cursorId, setCursorId] = useState(0);
    const [open, setOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [fetch, {data, isFetching, isLoading}] = fetchFn();

    const debouncedFetch = useMemo(() =>
            debounce(async (params) => {
                await fetch(params);
            }, 300),
        [fetch]);

    useEffect(() => {
        if (!open) return;

        debouncedFetch({
            body: {
                searchTerms: {
                    fullName: searchTerm
                },
                cursorId,
                pageSize: PAGE_SIZE
            },
            ...fetchFnProps
        });
    }, [open, cursorId, searchTerm, debouncedFetch]);

    useEffect(() => {
        if (!open || !data || isLoading || isFetching || !data.length) return;

        const isNewPage = cursorId === 0 || data[0].id > cursorId;

        if (!isNewPage) return;

        if (cursorId === 0) {
            setOptions(data);
        } else {
            setOptions(prev => [...prev, ...data]);
        }

        setHasMore(data.length === PAGE_SIZE);
    }, [data, open, cursorId, isLoading, isFetching]);

    const handleInputChange = (_event, newValue, reason) => {
        if (reason === 'input') {
            setSearchTerm(newValue);
            setOptions([]);
            setCursorId(0);
            setHasMore(true);
        }
    };

    const handleLoadMore = () => {
        if (hasMore) {
            setCursorId(options[options.length - 1].id);
        }
    };

    const displayOptions = useMemo(() => {
        if (multiple) {
            return options;
        }

        if (!value?.id) {
            return options;
        }

        if (options.some(option => option.id === value.id)) {
            return options;
        }

        return [value, ...options];
    }, [multiple, options, value]);

    const inputValue = multiple || open
        ? searchTerm
        : (value ? getOptionLabel(value) : '');

    return (
        <Autocomplete
            readOnly={isSelectionDisabled}
            multiple={multiple}
            disableCloseOnSelect={multiple}
            limitTags={multiple ? TAGS_SHOWN : undefined}
            disablePortal={disablePortal}
            slotProps={{
                popper: {
                    placement: popperPlacement,
                    modifiers: popperModifiers
                }
            }}
            open={open}
            value={value}
            inputValue={inputValue}
            filterOptions={(x) => x}
            onChange={(event, newValue) => {
                onChange(event, newValue);
                setSearchTerm('');
            }}
            onOpen={() => {
                setSearchTerm('');
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
                setSearchTerm('');
                setCursorId(0);
                setOptions([]);
                setHasMore(true);
            }}
            options={displayOptions}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, optionValue) => option.id === optionValue?.id}
            loading={isFetching || isLoading}
            onInputChange={handleInputChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isFetching || isLoading ? <CircularProgress size={20}/> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            ListboxProps={{
                sx: {maxHeight: '350px'},
                onScroll: (event) => {
                    const node = event.currentTarget;

                    if (
                        !isFetching &&
                        hasMore &&
                        node.scrollTop + node.clientHeight >= node.scrollHeight - 50
                    ) {
                        handleLoadMore();
                    }
                },
            }}
            // AD-2749: the box always shows the first five names and "+N", also while it is open, so it never
            // grows over its own list however many people are chosen.
            renderTags={multiple
                ? (tags, getTagProps) => (
                    <>
                        {tags.slice(0, TAGS_SHOWN).map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return <Chip key={key} size="small" label={getOptionLabel(option)} {...tagProps}/>;
                        })}
                        {tags.length > TAGS_SHOWN && (
                            <Box component="span" sx={{mx: 0.5, color: 'text.secondary', fontSize: 14}}>
                                +{tags.length - TAGS_SHOWN}
                            </Box>
                        )}
                    </>
                )
                : undefined}
            renderOption={multiple
                ? (props, option, {selected}) => (
                    <li {...props} key={option.id}>
                        <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                            checkedIcon={<CheckBoxIcon fontSize="small"/>}
                            checked={selected}
                            disabled={isSelectionDisabled}
                        />
                        {getOptionLabel(option)}
                    </li>
                )
                : undefined}
        />
    );
};

PaginatedAutocomplete.propTypes = {
    fetchFn: PropTypes.func,
    fetchFnProps: PropTypes.object,
    getOptionLabel: PropTypes.func,
    value: PropTypes.any,
    onChange: PropTypes.func,
    textFieldProps: PropTypes.object,
    disablePortal: PropTypes.bool,
    isSelectionDisabled: PropTypes.bool,
    popperPlacement: PropTypes.string,
    popperModifiers: PropTypes.array,
    multiple: PropTypes.bool
};

export default PaginatedAutocomplete;
